import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material"
import { useContext } from "react"
import { useTranslation } from "react-i18next"
import {
  useClearRefinements,
  useRefinementList,
  useStats,
  useToggleRefinement,
} from "react-instantsearch-core"

import Label from "@sap/components/label/Label"
import useResponsive from "@sap/hooks/useResponsive"

import { ItemsContext } from "@/pages/items/context"
import { DomainContext } from "@/routes/providers/originals"

export default function Filters() {
  return (
    <>
      <FilterDrawer />
      <FilterSideBar />
    </>
  )
}

function FilterDrawer() {
  const downMd = useResponsive("down", "md")
  const { t } = useTranslation("items")
  const { showFilters, setShowFilters } = useContext(ItemsContext)
  const { nbHits } = useStats()
  const { refine: clearFilters, canRefine } = useClearRefinements()

  return (
    <Drawer
      anchor={"bottom"}
      open={showFilters && downMd}
      onClose={() => {
        setShowFilters(false)
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "calc(100% - 64px)",
        },
      }}
    >
      <Stack
        sx={{
          paddingX: 4,
          paddingTop: 3,
          paddingBottom: 13,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
        divider={<Divider />}
        spacing={3}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" component="h2">
            Filters
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            <strong>{nbHits || 0}</strong> {t("Results")}
          </Typography>
        </Stack>
        <Categories />
        <Properties />
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          padding: 2,
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          transform: "translate3d(0,0,0)",
          overflow: "scroll",
        }}
      >
        <Button
          variant="soft"
          fullWidth
          color="inherit"
          size="large"
          disabled={!canRefine}
          onClick={() => {
            clearFilters()
            setShowFilters(false)
          }}
        >
          {t("ResetFilters")}
        </Button>
        <Button
          variant="soft"
          fullWidth
          color="primary"
          size="large"
          onClick={() => {
            setShowFilters(false)
          }}
        >
          {t("ShowResults")}
        </Button>
      </Stack>
    </Drawer>
  )
}

function FilterSideBar() {
  return (
    <Stack
      spacing={3}
      divider={<Divider />}
      sx={{
        paddingRight: 4,
        width: "280px",
        flexShrink: 0,

        display: {
          md: "block",
          xs: "none",
        },
      }}
    >
      <Categories />
      <Properties />
    </Stack>
  )
}

function Categories() {
  const { t } = useTranslation("items")
  const {
    items,
    refine: refineCategory,
    canRefine: hasCategory,
  } = useRefinementList({
    attribute: "category_plural",
    sortBy: ["name:asc"],
    limit: 20,
  })

  if (!hasCategory) {
    return null
  }

  return (
    <Box>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          textTransform: "uppercase",
        }}
      >
        {t("category")}
      </Typography>
      <Stack
        sx={{
          paddingTop: 1.5,
        }}
      >
        {items.map((category) => (
          <FormGroup key={category.label}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={category.isRefined}
                  onChange={() => refineCategory(category.value)}
                  size="small"
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
              label={
                <>
                  {category.label}
                  <Label
                    sx={{
                      height: "22px",
                      padding: "0px 6px",
                      marginLeft: "7px",
                      cursor: "inherit",
                    }}
                  >
                    {category.count}
                  </Label>
                </>
              }
            />
          </FormGroup>
        ))}
      </Stack>
    </Box>
  )
}

function Properties() {
  const { t } = useTranslation("items")
  const { isOriginals } = useContext(DomainContext)

  const { value: brandNew, refine: refineBrandNew } = useToggleRefinement({
    attribute: "brand_new",
  })

  const { value: outdoor, refine: refineOutdoor } = useToggleRefinement({
    attribute: "outdoor",
  })

  const { value: indoor, refine: refineIndoor } = useToggleRefinement({
    attribute: "indoor",
  })

  const { value: stackable, refine: refineStackable } = useToggleRefinement({
    attribute: "stackable",
  })

  const { value: sustainable, refine: refineSustainable } = useToggleRefinement({
    attribute: "sustainable",
  })

  const { value: satellietOriginals, refine: refineSatellietOriginals } = useToggleRefinement({
    attribute: "satelliet_originals",
  })
  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{
          textTransform: "uppercase",
        }}
      >
        {t("Properties")}
      </Typography>
      <Stack
        sx={{
          paddingTop: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={brandNew.isRefined}
              onChange={(e) => refineBrandNew({ isRefined: !e.target.checked })}
              size="small"
              icon={<CheckboxIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
            />
          }
          label={
            <>
              {t("brand_new")}
              {!brandNew.isRefined && (
                <Label
                  sx={{
                    height: "22px",
                    padding: "0px 6px",
                    marginLeft: "7px",
                    cursor: "inherit",
                  }}
                >
                  {brandNew.count || 0}
                </Label>
              )}
            </>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={indoor.isRefined}
              onChange={(e) => refineIndoor({ isRefined: !e.target.checked })}
              size="small"
              icon={<CheckboxIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
            />
          }
          label={
            <>
              {t("indoor")}
              {!indoor.isRefined && (
                <Label
                  sx={{
                    height: "22px",
                    padding: "0px 6px",
                    marginLeft: "7px",
                    cursor: "inherit",
                  }}
                >
                  {indoor.count || 0}
                </Label>
              )}
            </>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={outdoor.isRefined}
              onChange={(e) => refineOutdoor({ isRefined: !e.target.checked })}
              size="small"
              icon={<CheckboxIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
            />
          }
          label={
            <>
              {t("outdoor")}
              {!outdoor.isRefined && (
                <Label
                  sx={{
                    height: "22px",
                    padding: "0px 6px",
                    marginLeft: "7px",
                    cursor: "inherit",
                  }}
                >
                  {outdoor.count || 0}
                </Label>
              )}
            </>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={stackable.isRefined}
              onChange={(e) => refineStackable({ isRefined: !e.target.checked })}
              size="small"
              icon={<CheckboxIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
            />
          }
          label={
            <>
              {t("stackable")}
              {!stackable.isRefined && (
                <Label
                  sx={{
                    height: "22px",
                    padding: "0px 6px",
                    marginLeft: "7px",
                    cursor: "inherit",
                  }}
                >
                  {stackable.count || 0}
                </Label>
              )}
            </>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sustainable.isRefined}
              onChange={(e) => refineSustainable({ isRefined: !e.target.checked })}
              size="small"
              icon={<CheckboxIcon />}
              checkedIcon={<CheckboxCheckedIcon />}
            />
          }
          label={
            <>
              {t("sustainable")}
              {!sustainable.isRefined && (
                <Label
                  sx={{
                    height: "22px",
                    padding: "0px 6px",
                    marginLeft: "7px",
                    cursor: "inherit",
                  }}
                >
                  {sustainable.count || 0}
                </Label>
              )}
            </>
          }
        />
        {!isOriginals && (
          <FormControlLabel
            control={
              <Checkbox
                checked={satellietOriginals.isRefined}
                onChange={(e) => refineSatellietOriginals({ isRefined: !e.target.checked })}
                size="small"
                icon={<CheckboxIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
              />
            }
            label={
              <>
                {t("satelliet_originals")}
                {!satellietOriginals.isRefined && (
                  <Label
                    sx={{
                      height: "22px",
                      padding: "0px 6px",
                      marginLeft: "7px",
                      cursor: "inherit",
                    }}
                  >
                    {satellietOriginals.count || 0}
                  </Label>
                )}
              </>
            }
          />
        )}
      </Stack>
    </Box>
  )
}

function CheckboxIcon() {
  return (
    <SvgIcon>
      <path d="M 16.548484,1.6764302 21.665,1.665 21.645582,21.652593 c 0,0 -2.267004,0.02494 -4.881782,0.0041 l -0.215316,0.005 H 6.6261706 c -2.6561217,-1.55e-4 -4.9561958,-0.0084 -4.9561958,-0.0084 l -0.00496,-4.988179 V 6.6720033 c -4.214e-4,-2.9842274 0.00906,-4.918338 0.00906,-4.918338 L 16.548484,1.6754308 Z M 3.1654659,3.1754019 c 0,0 -0.012105,1.7896333 -0.012105,3.9972579 V 20.177197 c 0,0 1.7769484,-0.01345 3.9689255,-0.01345 H 20.014043 c 0,0 0.0072,-1.789633 0.0072,-3.997258 V 3.1631452 c -4.299594,0.018855 -21.1554491,0.012257 -16.8557771,0.012257 z" />
    </SvgIcon>
  )
}

function CheckboxCheckedIcon() {
  return (
    <SvgIcon>
      <path d="m 16.650337,1.665 c 2.758724,0 4.995112,0.00913 4.995112,0.00913 L 21.665,21.62587 c 2e-6,3e-6 -2.255939,0.02869 -5.014663,0.02869 H 6.6601123 C 3.901388,21.654565 1.665,21.665 1.665,21.665 V 6.6623911 C 1.665,3.9024083 1.6936775,1.665 1.6936775,1.665 Z M 15.026925,8.9162145 10.900963,13.04406 9.1526733,11.294973 c -0.3430279,-0.339945 -0.8957599,-0.339945 -1.2387878,0 -0.3396677,0.339822 -0.3396677,0.889535 0,1.239353 l 2.3776735,2.368763 c 0.169834,0.169911 0.389619,0.24987 0.609404,0.24987 0.229775,0 0.44956,-0.07996 0.619393,-0.24987 l 4.745357,-4.747522 c 0.339668,-0.3398221 0.339668,-0.8895351 0,-1.2393525 -0.343028,-0.339945 -0.895759,-0.339945 -1.238788,0 z" />
    </SvgIcon>
  )
}
